/**
 * Typed file-transfer API errors + the named discriminants every §5–§7 resume
 * branch in docs/file-transfer-resume.md dispatches on (§4.4). Extracted from
 * `apps/extension/src/background/api.ts` (TASK-27 M4 extraction) — this is a
 * pure dep of the moved resume/upload/receive cores (`sendFile.ts`,
 * `uploadLoop.ts`, `senderResume.ts`, `receiveResume.ts`), which discriminate
 * on these functions to route NETWORK / DEFINITIVE_404 / ALREADY_COMPLETE /
 * DUPLICATE_TRANSFER outcomes.
 *
 * Each surface (extension `background/api.ts`, web `api/client.ts` /
 * `filetransfer/api.ts`) constructs its OWN per-endpoint HTTP wrapper
 * functions (different token/API_BASE plumbing per app), but all of them
 * throw/classify via this ONE shared `TransferApiError` + discriminant set so
 * the shared resume algorithms behave identically regardless of which app's
 * fetch wrapper produced the error.
 */

/**
 * Thrown by every file-transfer HTTP wrapper (docs/file-transfer-resume.md
 * §4.4). `status` is 0 for a network-level failure (fetch rejected/aborted);
 * `code` is the server body's `error` field, `"presigned"` for a non-2xx R2
 * PUT/GET (no JSON body), `"network"` when `status` is 0, or `"unknown"` when
 * the body is missing/unparseable.
 */
export class TransferApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = "TransferApiError";
  }
}

// --- Named discriminants (§4.4 table) — the ONLY error tests the §5–§7
// resume branches are allowed to use.

/**
 * API-origin 404 (server body `error: "not_found"`) — the only trigger for
 * the §7.2 EXPIRED path (subject to §6.3's CREATE_WAIT exemption on the
 * receiver side). A presigned 404 (`code === "presigned"`, R2 object missing
 * despite the API's word) is NOT definitive — it stays retryable like any
 * other presigned failure.
 */
export function isDefinitive404(err: unknown): boolean {
  return err instanceof TransferApiError && err.status === 404 && err.code !== "presigned";
}

/** A network-level failure (fetch rejected/aborted/timed out) — retry per budget; never terminal, never EXPIRED. */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TransferApiError && err.status === 0;
}

/** upload-url/uploaded called after the transfer's own `/complete` already ran — re-GET (§5.2 step 5). */
export function isAlreadyComplete(err: unknown): boolean {
  return (
    err instanceof TransferApiError &&
    err.status === 409 &&
    err.code === "transfer_already_complete"
  );
}

/** create-only; unreachable in resume flows (records exist only post-201) — if ever seen, terminal "failed"/"INTERNAL". */
export function isDuplicateTransfer(err: unknown): boolean {
  return err instanceof TransferApiError && err.status === 409 && err.code === "duplicate_transfer";
}

/**
 * `createTransfer`-only (TASK-30): the server's daily file-transfer byte quota rejection
 * (`apps/server/src/routes/transfers.ts`'s `quota_exceeded` case, 429 + `Retry-After`). This is the
 * ONLY create-time rejection that maps to a named `FtErrorCode` (`QUOTA_EXCEEDED`) the UI shows a
 * distinct "daily limit reached" string for (docs/file-transfer-architecture.md §8); every other
 * create-time rejection (`concurrent_limit_exceeded`, `file_too_large`, `not_paired_with_target`,
 * network) falls back to the generic `INTERNAL` bucket — see `sendFile.ts`'s `createTransfer` call
 * site.
 */
export function isQuotaExceeded(err: unknown): boolean {
  return err instanceof TransferApiError && err.status === 429 && err.code === "quota_exceeded";
}
